/**
 * 🔒 An administrator deletes a link made by mistake (T728; decision 1500).
 * Every link between two records closes with an end date for ordinary use; a
 * holder of `records.links.delete` removes one that was never true, from its
 * row, with a typed reason. One rule covers seven link kinds, and the server
 * decides which rows carry the control.
 */

/** The capability the route asks and the tiles read; seeded on the Admin role alone. */
export const LINK_DELETE_CAPABILITY = 'records.links.delete';

/** The seven link kinds the rule covers, as the route's `:kind` segment names them; `site` is a company's occupancy of a building (decision 7380). */
export const LINK_KINDS = ['employment', 'siteRole', 'companyOwnership', 'assetCompany', 'custody', 'component', 'site'] as const;
export type LinkKind = (typeof LINK_KINDS)[number];

export function isLinkKind(value: unknown): value is LinkKind {
  return typeof value === 'string' && (LINK_KINDS as readonly string[]).includes(value);
}

/** The longest reason the window takes; the audit row keeps it whole. */
export const LINK_DELETE_REASON_MAX = 500;

/** What the route refuses before it reads anything: a reason that is empty once trimmed, or one longer than the column takes. */
export function linkDeleteReasonProblem(reason: unknown): string | null {
  if (typeof reason !== 'string' || reason.trim() === '') return 'A deleted link records why it was deleted; type the reason.';
  if (reason.trim().length > LINK_DELETE_REASON_MAX) return `A reason is at most ${LINK_DELETE_REASON_MAX} characters.`;
  return null;
}

/**
 * The facts a tile row carries so the page offers Delete this link on it: the
 * kind, the link's own id (a row's id is sometimes a position's), the words
 * the window names the link by, and whether this viewer may delete it.
 */
export interface LinkRowFacts {
  [key: string]: string | boolean;
  linkKind: LinkKind;
  linkId: string;
  linkLabel: string;
  linkDeletable: boolean;
}

export function linkRowFacts(kind: LinkKind, linkId: string, linkLabel: string, deletable: boolean): LinkRowFacts {
  return { linkKind: kind, linkId, linkLabel, linkDeletable: deletable };
}

/** The link a row offers to delete, read back from its facts; null where the row offers none to this viewer. */
export interface LinkTarget {
  kind: LinkKind;
  id: string;
  label: string;
}

export function linkTargetOf(raw: Readonly<Record<string, unknown>> | null | undefined): LinkTarget | null {
  if (!raw || raw.linkDeletable !== true || !isLinkKind(raw.linkKind) || typeof raw.linkId !== 'string' || raw.linkId === '') return null;
  return { kind: raw.linkKind, id: raw.linkId, label: typeof raw.linkLabel === 'string' ? raw.linkLabel : '' };
}

/** The route a delete posts to, relative to the API root. */
export function linkDeletePath(kind: LinkKind, linkId: string): string {
  return `/links/${encodeURIComponent(kind)}/${encodeURIComponent(linkId)}/delete`;
}

/** The changed field the audit row names what the row said under, which the History line reads back. */
export const LINK_DELETED_FIELD = 'link';

/** What a deleted link's History row reads after Link deleted: the words the audit row kept, or nothing. */
export function linkDeletedSaid(changedFields: unknown): string | null {
  if (!Array.isArray(changedFields)) return null;
  const said = changedFields.find((row: { field?: unknown }) => row?.field === LINK_DELETED_FIELD) as { from?: unknown } | undefined;
  return typeof said?.from === 'string' && said.from !== '' ? said.from : null;
}

/**
 * 🔒 The changed fields an employment's deletion lists what went with it under
 * (decision 1500): one per position, its title and dates, and one per contact
 * method, its kind and value. Piece 13 keeps an ended contact method answerable,
 * so a deletion that takes one says so on the record.
 */
export const LINK_DELETED_POSITION_FIELD = 'position';
export const LINK_DELETED_CONTACT_FIELD = 'contactMethod';
/** 🔒 And a deleted site's own rows (decision 7380): one per role, requirement and policy. */
export const LINK_DELETED_ROLE_FIELD = 'siteRole';
export const LINK_DELETED_REQUIREMENT_FIELD = 'siteRequirement';
export const LINK_DELETED_POLICY_FIELD = 'sitePolicy';
/** 🔒 And the rows that hang from a deleted site alone: a custom-field value, and a file or a note linked to it as also about it. */
export const LINK_DELETED_ATTRIBUTE_FIELD = 'customField';
export const LINK_DELETED_FILE_LINK_FIELD = 'fileLink';
export const LINK_DELETED_NOTE_LINK_FIELD = 'noteLink';

const WENT_FIELDS: readonly string[] = [LINK_DELETED_POSITION_FIELD, LINK_DELETED_CONTACT_FIELD, LINK_DELETED_ROLE_FIELD, LINK_DELETED_REQUIREMENT_FIELD, LINK_DELETED_POLICY_FIELD, LINK_DELETED_ATTRIBUTE_FIELD, LINK_DELETED_FILE_LINK_FIELD, LINK_DELETED_NOTE_LINK_FIELD];

/** What went with a deleted employment or site, read back from the audit row in the order it was written; empty for every other link. */
export function linkDeletedWent(changedFields: unknown): string[] {
  if (!Array.isArray(changedFields)) return [];
  return changedFields
    .filter((row: { field?: unknown; from?: unknown }) => typeof row?.field === 'string' && WENT_FIELDS.includes(row.field) && typeof row.from === 'string' && row.from !== '')
    .map((row: { from: string }) => row.from);
}
